/**
 * /auth/verify?token=... — verifies email by magic-link token (US-E1-001).
 * Q119-Q121 protocol №030 — third placement of <ResendVerificationButton />:
 *   on expired/invalid token, prompt user to request a fresh link.
 *
 * The user may not be present in this tab (clicked from email client),
 * so on expired we ask for email again before resend.
 */
import { useEffect, useState } from 'react'
import { authApi } from '@/api/auth'
import { ApiError } from '@/api/client'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { ResendVerificationButton } from '@/components/auth/ResendVerificationButton'
import { Icon } from '@/components/icons/Icon'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/auth/TextInput'

type State =
  | { kind: 'verifying' }
  | { kind: 'ok'; email?: string }
  | { kind: 'expired' }
  | { kind: 'error' }

export function Verify() {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') ?? ''
  const [state, setState] = useState<State>({ kind: 'verifying' })
  const [email, setEmail] = useState('')

  useEffect(() => {
    if (!token) {
      setState({ kind: 'expired' })
      return
    }
    authApi
      .verifyEmail(token)
      .then((res) => setState({ kind: 'ok', email: res.email }))
      .catch((e) => {
        const err = e as ApiError
        if (err.status === 410 || err.code === 'token_expired') setState({ kind: 'expired' })
        else setState({ kind: 'error' })
      })
  }, [token])

  return (
    <AuthLayout>
      {state.kind === 'verifying' && (
        <div className="text-center space-y-4">
          <div className="inline-block w-10 h-10 border-2 border-accent-brand-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-text-secondary">Проверяем ссылку...</p>
        </div>
      )}

      {state.kind === 'ok' && (
        <div className="space-y-6 text-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-status-success-bg mx-auto">
            <Icon name="check" size={32} className="text-status-success-text" aria-hidden="true" />
          </div>
          <h1 className="text-3xl font-bold font-display text-text-primary">Email подтверждён ✅</h1>
          <p className="text-text-secondary">Всё готово. CEO ждёт первой задачи.</p>
          <Button asChild variant="primary" size="lg">
            <a href={state.email ? `/auth/login?email=${encodeURIComponent(state.email)}&verified=1` : '/auth/login?verified=1'}>
              Войти
            </a>
          </Button>
        </div>
      )}

      {state.kind === 'expired' && (
        <div className="space-y-6">
          <h1 className="text-3xl font-bold font-display text-text-primary text-center lg:text-left">Ссылка устарела ⏰</h1>
          <p className="text-text-secondary text-center lg:text-left">
            Письмо подтверждения действует 1 час. Введи email — пришлём свежую ссылку.
          </p>
          <div className="space-y-3">
            <TextInput
              id="verify-email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={setEmail}
              placeholder="alex@company.ru"
            />
            {email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && (
              <ResendVerificationButton email={email.toLowerCase().trim()} variant="block" />
            )}
          </div>
          <a href="/auth/login" className="block text-center text-text-link font-semibold">
            Войти
          </a>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="space-y-6 text-center">
          <h1 className="text-3xl font-bold font-display text-text-primary">Что-то пошло не так 😕</h1>
          <p className="text-text-secondary">
            Не удалось подтвердить email. Попробуй позже или напиши на{' '}
            <a href="mailto:support@agentceo.ru" className="text-text-link font-semibold">
              support@agentceo.ru
            </a>
            .
          </p>
        </div>
      )}
    </AuthLayout>
  )
}
